import { useEffect, useState } from "react";
import { Zap } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Popover as AriaPopover,
} from "react-aria-components";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { formatLogInstant } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";
import { useFeature } from "@/lib/license";
import {
  COMM_LOG_LEVELS,
  PERF_MODE_DURATIONS,
  formatRemaining,
  useDisablePerformanceMode,
  useEnablePerformanceMode,
  usePerformanceMode,
  type CommLogLevel,
} from "@/lib/performanceMode";
import { cx } from "@/utils/cx";

/**
 * Režim Performance v horní liště.
 *
 * Neaktivní je to tichá ikona v řadě ostatních; **aktivní je to výrazná pilulka
 * s odpočtem**, protože potlačené logování je dočasné oslabení auditní stopy a nesmí
 * se dát přehlédnout. Odpočet tiká lokálně z `expiresAtEpochMs` — server se kvůli němu
 * nedotazuje každou vteřinu a po zapnutí naskočí okamžitě, bez čekání na další dotaz.
 *
 * Obsah popoveru **musí být `Dialog`**: bez něj react-aria spolkne kliknutí dovnitř
 * (táž past je popsaná u `NotificationBell`).
 */
export function PerformanceModeButton() {
  const { t } = useTranslation();
  const { visibility } = useFeature("admin.performance-mode");
  const { data } = usePerformanceMode();

  // Default-deny: chybějící feature = HIDDEN, tlačítko se nevykreslí.
  if (visibility !== "ENABLED") {
    return null;
  }

  const active = data?.active ?? false;

  return (
    <AriaDialogTrigger>
      {/*
        Tooltip uvnitř DialogTriggeru: `aria-label` vidí jen odečítač obrazovky, ale
        v řadě ikon vedle sebe není myší poznat, co která dělá. Stejný důvod jako
        u `IconAction`. Nativní `title` na react-aria komponentě neprojde.
      */}
      <Tooltip
        title={active ? t("perfMode.activeTitle") : t("perfMode.title")}
        description={
          active && data
            ? t("perfMode.tooltipActive", {
                level: t(`perfMode.level.${data.level}`),
                since: formatLogInstant(data.startedAtEpochMs),
              })
            : t("perfMode.subtitle")
        }
        placement="bottom"
      >
        <AriaButton
          aria-label={t("perfMode.title")}
          className={cx(
            "flex cursor-pointer items-center gap-1.5 rounded-md outline-focus-ring transition",
            "focus-visible:outline-2 focus-visible:outline-offset-2",
            active
              ? "bg-warning-primary px-2 py-1.5 text-warning-primary ring-1 ring-warning"
              : "p-2 text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover",
          )}
        >
          <Zap className="size-5 shrink-0" aria-hidden="true" />
          {active && data && <Countdown expiresAtEpochMs={data.expiresAtEpochMs} />}
        </AriaButton>
      </Tooltip>

      <AriaPopover placement="bottom right" offset={8} className="z-50">
        <AriaDialog className="w-88 rounded-xl bg-primary p-4 shadow-lg ring-1 ring-secondary outline-hidden">
          {({ close }) => <PerformanceModePanel onClose={close} />}
        </AriaDialog>
      </AriaPopover>
    </AriaDialogTrigger>
  );
}

/** Odpočet tikající v prohlížeči — server se kvůli němu nedotazuje. */
function Countdown({ expiresAtEpochMs }: { expiresAtEpochMs: number }) {
  const [remaining, setRemaining] = useState(() => expiresAtEpochMs - Date.now());

  useEffect(() => {
    setRemaining(expiresAtEpochMs - Date.now());
    const id = window.setInterval(() => setRemaining(expiresAtEpochMs - Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAtEpochMs]);

  return (
    <span className="font-mono text-xs tabular-nums" data-testid="perf-mode-countdown">
      {formatRemaining(remaining)}
    </span>
  );
}

function PerformanceModePanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = usePerformanceMode();
  const enable = useEnablePerformanceMode();
  const disable = useDisablePerformanceMode();

  const [level, setLevel] = useState<CommLogLevel>("ERRORS_ONLY");
  const [minutes, setMinutes] = useState(30);
  const [goToStats, setGoToStats] = useState(false);

  if (data?.active) {
    return (
      <div className="flex flex-col gap-3">
        <div>
          <p className="text-sm font-semibold text-primary">{t("perfMode.activeTitle")}</p>
          <p className="mt-1 text-sm text-tertiary">
            {t("perfMode.activeDetail", { level: t(`perfMode.level.${data.level}`) })}
          </p>
        </div>

        <dl className="flex flex-col gap-1 rounded-lg bg-secondary p-3 text-xs">
          <Row label={t("perfMode.remaining")} value={formatRemaining(data.expiresAtEpochMs - Date.now())} />
          {/*
            Okamžik zapnutí je tu proto, že zápis do logu je asynchronní: řádky vzniklé
            těsně před přepnutím se v prohlížeči objeví až po něm a bez tohohle času to
            vypadá, jako by režim nefungoval. Rozdíl můžou být sekundy.
          */}
          <Row label={t("perfMode.startedAt")} value={formatLogInstant(data.startedAtEpochMs)} />
          {data.startedBy && <Row label={t("perfMode.startedBy")} value={data.startedBy} />}
          {data.suppressNotifications && (
            <Row label={t("perfMode.suppressed")} value={String(data.suppressedNotifications)} />
          )}
        </dl>

        <p className="text-xs text-tertiary">{t("perfMode.queueHint")}</p>

        <div className="flex gap-2">
          <Button size="sm" color="secondary" className="flex-1" onClick={() => { navigate({ to: "/server-stats" }); onClose(); }}>
            {t("perfMode.goToStats")}
          </Button>
          <Button
            size="sm"
            color="primary"
            className="flex-1"
            isDisabled={disable.isPending}
            onClick={() => disable.mutate(undefined, { onSuccess: () => onClose() })}
          >
            {t("perfMode.stop")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-sm font-semibold text-primary">{t("perfMode.title")}</p>
        <p className="mt-1 text-sm text-tertiary">{t("perfMode.subtitle")}</p>
      </div>

      <label className="flex flex-col gap-1 text-xs font-medium text-secondary">
        {t("perfMode.levelLabel")}
        <AppSelect value={level} onChange={(e) => setLevel(e.target.value as CommLogLevel)}>
          {COMM_LOG_LEVELS.filter((l) => l !== "FULL").map((l) => (
            <option key={l} value={l}>
              {t(`perfMode.level.${l}`)}
            </option>
          ))}
        </AppSelect>
        <span className="font-normal text-tertiary">{t(`perfMode.levelHint.${level}`)}</span>
      </label>

      <label className="flex flex-col gap-1 text-xs font-medium text-secondary">
        {t("perfMode.durationLabel")}
        <AppSelect value={String(minutes)} onChange={(e) => setMinutes(Number(e.target.value))}>
          {PERF_MODE_DURATIONS.map((m) => (
            <option key={m} value={m}>
              {t("perfMode.minutes", { count: m })}
            </option>
          ))}
        </AppSelect>
      </label>

      <label className="flex items-center gap-2 text-xs text-secondary">
        <input
          type="checkbox"
          checked={goToStats}
          onChange={(e) => setGoToStats(e.target.checked)}
          className="size-4 rounded border-primary"
        />
        {t("perfMode.goToStatsAfterStart")}
      </label>

      <p className="text-xs text-tertiary">{t("perfMode.autoExpiryHint")}</p>

      <Button
        size="sm"
        color="primary"
        isDisabled={enable.isPending}
        onClick={() =>
          enable.mutate(
            { level, durationMinutes: minutes, suppressNotifications: true },
            {
              onSuccess: () => {
                if (goToStats) navigate({ to: "/server-stats" });
                onClose();
              },
            },
          )
        }
      >
        {t("perfMode.start")}
      </Button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-tertiary">{label}</dt>
      <dd className="font-medium text-primary">{value}</dd>
    </div>
  );
}
